import React from 'react';
import {
  ResponsiveContainer, BarChart, Bar, LineChart, Line, AreaChart, Area,
  PieChart, Pie, Cell, FunnelChart, Funnel, LabelList, ComposedChart,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceLine,
} from 'recharts';

export const CHART_COLORS = [
  '#6366f1', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981',
  '#06b6d4', '#ef4444', '#3b82f6', '#84cc16', '#f97316',
];

const fmt = (n) => {
  if (n == null) return '0';
  if (Math.abs(n) >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (Math.abs(n) >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return `${n}`;
};

const RADIAN = Math.PI / 180;

// Ellipsis-truncate a label so long category names ("OnePiece Corp",
// "Google Ads — North America") don't blow past the chart edge or each
// other.
const truncateLabel = (s, n = 14) => {
  const str = s == null ? '' : String(s);
  return str.length > n ? `${str.slice(0, n - 1)}…` : str;
};

// Pie label tuning. Slices below MIN_LABEL_PERCENT never get an inline
// label (they'd collide into an unreadable stack — see the reported
// overlap bug); the legend + hover tooltip still surface them. Past
// LEGEND_ONLY_THRESHOLD categories we drop inline labels entirely and
// lean on the legend, Salesforce-style.
const PIE_MIN_LABEL_PERCENT = 0.05;
const PIE_LEGEND_ONLY_THRESHOLD = 8;

/**
 * Renders a single chart from aggregated [{name,value,count}] data.
 *
 * Props:
 *   type             — 'bar' | 'line' | 'area' | 'pie' | 'donut' | 'funnel'
 *                      | 'heatmap' | 'kpi'.
 *   data             — aggregated series.
 *   height           — pixel height.
 *   valueLabel       — fallback Y label (used by Line/Bar/Area name prop and
 *                      the KPI label). Falls back to yAxisLabel when set.
 *   xAxisLabel       — optional X-axis title for cartesian charts.
 *   yAxisLabel       — optional Y-axis title for cartesian charts.
 *   showLegend       — defaults true. Hides the recharts Legend.
 *   showGrid         — defaults true. Hides the CartesianGrid on x/y charts.
 *   showTooltip      — defaults true. Hides the hover Tooltip.
 *   onSliceClick     — drill-down handler. Fires with the clicked category.
 */
/**
 * Extended props (Charts 2.0):
 *   seriesKeys   — breakdown series names (from chartData.seriesKeys);
 *                  turns bar charts into grouped/stacked multi-series.
 *   stacked      — stack the breakdown series.
 *   stack100     — normalise stacks to 100%.
 *   goalValue    — renders a labelled reference line on cartesian charts.
 *   valueLabel2  — legend label for the combo chart's second measure.
 *   colorRules   — KPI conditional colors: [{operator, value, color}],
 *                  first matching rule wins (operators: gte, gt, lte, lt, eq).
 */
const ChartRenderer = ({
  type, data, height = 260, valueLabel = 'Value', onSliceClick,
  xAxisLabel, yAxisLabel,
  showLegend = true, showGrid = true, showTooltip = true,
  seriesKeys = null, stacked = false, stack100 = false,
  goalValue = null, valueLabel2 = 'Secondary', colorRules = null,
}) => {
  // Recharts axis-label config helpers. Returning undefined when the user
  // hasn't filled the label means recharts skips rendering the prop —
  // important because an empty string still reserves space.
  const xLabelProp = xAxisLabel
    ? { value: xAxisLabel, position: 'insideBottom', offset: -2, fontSize: 11, fill: '#64748b' }
    : undefined;
  const yLabelProp = yAxisLabel
    ? { value: yAxisLabel, angle: -90, position: 'insideLeft', fontSize: 11, fill: '#64748b', offset: 10 }
    : undefined;
  const yName = yAxisLabel || valueLabel;
  const cartesianClick = onSliceClick
    ? (e) => { if (e && e.activeLabel != null) onSliceClick(String(e.activeLabel)); }
    : undefined;
  const pieClick = onSliceClick ? (d) => { if (d && d.name != null) onSliceClick(String(d.name)); } : undefined;
  const funnelClick = onSliceClick
    ? (d) => {
      const n = d && (d.name != null ? d.name : (d.payload && d.payload.name));
      if (n != null) onSliceClick(String(n));
    }
    : undefined;
  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center text-sm text-slate-400" style={{ height }}>
        No data to chart — add a grouping and a measure.
      </div>
    );
  }

  if (type === 'kpi') {
    const total = data.reduce((s, d) => s + (d.value || 0), 0);
    // Conditional color: first matching rule wins; default indigo.
    let kpiColor = '#4f46e5';
    for (const rule of (colorRules || [])) {
      const rv = Number(rule?.value);
      if (Number.isNaN(rv)) continue;
      const op = rule.operator || 'gte';
      const hit = (op === 'gte' && total >= rv) || (op === 'gt' && total > rv)
        || (op === 'lte' && total <= rv) || (op === 'lt' && total < rv)
        || (op === 'eq' && total === rv);
      if (hit) { kpiColor = rule.color || kpiColor; break; }
    }
    const vsGoal = goalValue != null && Number(goalValue) !== 0
      ? (total / Number(goalValue)) * 100 : null;
    return (
      <div className="flex flex-col items-center justify-center" style={{ height }}>
        <div className="text-4xl font-bold" style={{ color: kpiColor }}>{fmt(total)}</div>
        <div className="text-sm text-slate-500 mt-1">{valueLabel}</div>
        {vsGoal != null && (
          <div className={`text-xs mt-1.5 font-medium ${vsGoal >= 100 ? 'text-emerald-600' : 'text-amber-600'}`}>
            {vsGoal.toFixed(0)}% of goal ({fmt(Number(goalValue))})
          </div>
        )}
        <div className="flex flex-wrap gap-2 mt-4 justify-center">
          {data.slice(0, 4).map((d, i) => (
            <div key={d.name} className="text-center px-3 py-1.5 bg-slate-50 rounded-lg">
              <div className="text-sm font-semibold" style={{ color: CHART_COLORS[i % CHART_COLORS.length] }}>{fmt(d.value)}</div>
              <div className="text-[10px] text-slate-500 max-w-[80px] truncate">{d.name}</div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (type === 'heatmap' && seriesKeys && seriesKeys.length) {
    // Matrix heatmap: rows = breakdown values, columns = groups, cells
    // colored by intensity. Powered by the breakdown series data.
    const allVals = data.flatMap((d) => Object.values(d.series || {})).map((v) => v || 0);
    const max = Math.max(...allVals, 1);
    return (
      <div className="overflow-auto p-1" style={{ maxHeight: height }}>
        <table className="border-separate" style={{ borderSpacing: 3 }}>
          <thead>
            <tr>
              <th className="text-[10px] text-slate-400 font-medium text-left pr-2" />
              {data.map((d) => (
                <th key={d.name} className="text-[10px] text-slate-500 font-medium px-1 max-w-[90px] truncate">
                  {truncateLabel(d.name, 12)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {seriesKeys.map((sk) => (
              <tr key={sk}>
                <td className="text-[10px] text-slate-500 font-medium pr-2 whitespace-nowrap max-w-[110px] truncate">
                  {truncateLabel(sk, 16)}
                </td>
                {data.map((d) => {
                  const v = (d.series || {})[sk] || 0;
                  const intensity = v / max;
                  return (
                    <td key={d.name}
                      onClick={onSliceClick ? () => onSliceClick(String(d.name)) : undefined}
                      className={`rounded-md text-center text-[10px] font-semibold px-2 py-1.5 min-w-[46px] ${
                        onSliceClick ? 'cursor-pointer' : ''} ${intensity > 0.5 ? 'text-white' : 'text-slate-700'}`}
                      style={{ background: `rgba(99,102,241,${0.08 + intensity * 0.82})` }}
                      title={`${sk} × ${d.name}: ${v}`}>
                      {fmt(v)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (type === 'heatmap') {
    const max = Math.max(...data.map((d) => d.value || 0), 1);
    return (
      <div className="grid grid-cols-3 gap-1.5 p-1" style={{ minHeight: height }}>
        {data.map((d) => {
          const intensity = (d.value || 0) / max;
          return (
            <div
              key={d.name}
              onClick={onSliceClick ? () => onSliceClick(String(d.name)) : undefined}
              className={`rounded-lg p-2 flex flex-col justify-between ${onSliceClick ? 'cursor-pointer hover:ring-2 hover:ring-indigo-400' : ''}`}
              style={{ background: `rgba(99,102,241,${0.12 + intensity * 0.78})` }}
            >
              <div className={`text-xs font-medium truncate ${intensity > 0.5 ? 'text-white' : 'text-slate-700'}`}>{d.name}</div>
              <div className={`text-sm font-bold ${intensity > 0.5 ? 'text-white' : 'text-slate-900'}`}>{fmt(d.value)}</div>
            </div>
          );
        })}
      </div>
    );
  }

  if (type === 'pie' || type === 'donut') {
    // Legend-only mode once there are too many categories to label inline
    // without collisions. Inline labels are then suppressed and the legend
    // (plus hover tooltip) carry the names — no overlapping text.
    const legendOnly = data.length > PIE_LEGEND_ONLY_THRESHOLD;
    const labelVisible = (percent) => !legendOnly && (percent ?? 0) >= PIE_MIN_LABEL_PERCENT;

    // Outside-positioned label with a leader line. Hidden for tiny slices
    // and in legend-only mode. textAnchor flips by side so labels read
    // outward and don't run back across the chart.
    const renderPieLabel = (p) => {
      const name = p.name != null ? p.name : p.payload?.name;
      if (!labelVisible(p.percent)) return null;
      const r = p.outerRadius + 16;
      const x = p.cx + r * Math.cos(-p.midAngle * RADIAN);
      const y = p.cy + r * Math.sin(-p.midAngle * RADIAN);
      return (
        <text
          x={x}
          y={y}
          textAnchor={x >= p.cx ? 'start' : 'end'}
          dominantBaseline="central"
          fontSize={11}
          fontWeight={500}
          fill="#475569"
        >
          {`${truncateLabel(name)} ${(p.percent * 100).toFixed(0)}%`}
        </text>
      );
    };
    const renderPieLabelLine = (p) => {
      if (!labelVisible(p.percent) || !p.points || p.points.length < 2) return null;
      return (
        <path
          d={`M${p.points[0].x},${p.points[0].y}L${p.points[1].x},${p.points[1].y}`}
          stroke="#cbd5e1"
          fill="none"
        />
      );
    };

    // Leave headroom for the outside labels so they aren't clipped at the
    // container edge. Legend-only mode can use a larger radius since no
    // labels sit outside the ring.
    const outerR = legendOnly ? height / 2.7 : height / 3.4;

    return (
      <ResponsiveContainer width="100%" height={height}>
        <PieChart margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
          <Pie
            data={data}
            dataKey="value"
            nameKey="name"
            cx="50%"
            cy="50%"
            outerRadius={outerR}
            innerRadius={type === 'donut' ? outerR * 0.58 : 0}
            paddingAngle={data.length > 1 ? 2 : 0}
            minAngle={2}
            label={renderPieLabel}
            labelLine={renderPieLabelLine}
            isAnimationActive={false}
            onClick={pieClick}
            cursor={onSliceClick ? 'pointer' : 'default'}
          >
            {data.map((d, i) => <Cell key={`${d.name ?? 'slice'}-${i}`} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
          </Pie>
          {/* Tooltip carries the name + value for slices whose inline
              label was hidden (tiny slices / legend-only mode). */}
          {showTooltip && <Tooltip formatter={(v, n) => [fmt(v), n]} />}
          {showLegend && (
            <Legend
              wrapperStyle={{ fontSize: 11 }}
              formatter={(value) => truncateLabel(value, 18)}
            />
          )}
        </PieChart>
      </ResponsiveContainer>
    );
  }

  if (type === 'funnel') {
    return (
      <ResponsiveContainer width="100%" height={height}>
        <FunnelChart>
          {showTooltip && <Tooltip formatter={(v) => fmt(v)} />}
          <Funnel dataKey="value" data={data} isAnimationActive onClick={funnelClick}
            cursor={onSliceClick ? 'pointer' : 'default'}>
            <LabelList position="right" fill="#475569" stroke="none" dataKey="name" fontSize={11} />
            <LabelList position="left" fill="#1e293b" stroke="none" dataKey="value" fontSize={11} formatter={(v) => fmt(v)} />
            {data.map((d, i) => <Cell key={d.name} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
          </Funnel>
        </FunnelChart>
      </ResponsiveContainer>
    );
  }

  // When the user has provided an axis label we add a little extra bottom
  // margin so the label doesn't get clipped at the bottom of the chart.
  const cartesianMargin = {
    top: 10, right: 16, bottom: xAxisLabel ? 20 : 4, left: yAxisLabel ? 14 : 0,
  };

  const goalLine = goalValue != null ? (
    <ReferenceLine
      y={Number(goalValue)}
      stroke="#f59e0b"
      strokeDasharray="6 4"
      strokeWidth={2}
      label={{ value: `Goal ${fmt(Number(goalValue))}`, position: 'insideTopRight', fontSize: 10, fill: '#b45309' }}
    />
  ) : null;

  if (type === 'combo') {
    return (
      <ResponsiveContainer width="100%" height={height}>
        <ComposedChart data={data} margin={cartesianMargin} onClick={cartesianClick}>
          {showGrid && <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />}
          <XAxis dataKey="name" fontSize={11} tick={{ fill: '#64748b' }} label={xLabelProp} />
          <YAxis yAxisId="left" fontSize={11} tick={{ fill: '#64748b' }} tickFormatter={fmt} label={yLabelProp} />
          <YAxis yAxisId="right" orientation="right" fontSize={11} tick={{ fill: '#8b5cf6' }} tickFormatter={fmt} />
          {showTooltip && <Tooltip formatter={(v) => fmt(v)} />}
          {showLegend && <Legend wrapperStyle={{ fontSize: 11 }} />}
          <Bar yAxisId="left" dataKey="value" fill="#6366f1" radius={[6, 6, 0, 0]} name={yName} />
          <Line yAxisId="right" type="monotone" dataKey="value2" stroke="#8b5cf6" strokeWidth={2.5} dot={{ r: 3 }} name={valueLabel2} />
          {goalLine && React.cloneElement(goalLine, { yAxisId: 'left' })}
        </ComposedChart>
      </ResponsiveContainer>
    );
  }

  // Multi-series (breakdown) bars — grouped by default, stacked or 100%
  // stacked when requested.
  if (type === 'bar' && seriesKeys && seriesKeys.length) {
    const rows = data.map((d) => ({ name: d.name, ...(d.series || {}) }));
    const pct = (v) => `${Math.round(v * 100)}%`;
    return (
      <ResponsiveContainer width="100%" height={height}>
        <BarChart
          data={rows}
          margin={cartesianMargin}
          onClick={cartesianClick}
          stackOffset={stack100 ? 'expand' : undefined}
        >
          {showGrid && <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />}
          <XAxis dataKey="name" fontSize={11} tick={{ fill: '#64748b' }} label={xLabelProp} />
          <YAxis fontSize={11} tick={{ fill: '#64748b' }} tickFormatter={stack100 ? pct : fmt} label={yLabelProp} />
          {showTooltip && (
            <Tooltip formatter={(v, n) => [stack100 ? fmt(v) : fmt(v), truncateLabel(n, 20)]} />
          )}
          {showLegend && <Legend wrapperStyle={{ fontSize: 11 }} formatter={(v) => truncateLabel(v, 16)} />}
          {seriesKeys.map((k, i) => (
            <Bar
              key={k}
              dataKey={k}
              stackId={stacked || stack100 ? 'stack' : undefined}
              fill={CHART_COLORS[i % CHART_COLORS.length]}
              radius={(stacked || stack100) && i === seriesKeys.length - 1 ? [6, 6, 0, 0] : (stacked || stack100) ? [0, 0, 0, 0] : [6, 6, 0, 0]}
              name={k}
            />
          ))}
          {!stack100 && goalLine}
        </BarChart>
      </ResponsiveContainer>
    );
  }

  if (type === 'line') {
    return (
      <ResponsiveContainer width="100%" height={height}>
        <LineChart data={data} margin={cartesianMargin} onClick={cartesianClick}>
          {showGrid && <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />}
          <XAxis dataKey="name" fontSize={11} tick={{ fill: '#64748b' }} label={xLabelProp} />
          <YAxis fontSize={11} tick={{ fill: '#64748b' }} tickFormatter={fmt} label={yLabelProp} />
          {showTooltip && <Tooltip formatter={(v) => fmt(v)} />}
          {showLegend && <Legend wrapperStyle={{ fontSize: 11 }} />}
          <Line type="monotone" dataKey="value" stroke="#6366f1" strokeWidth={2.5} dot={{ r: 3 }} name={yName} />
          {goalLine}
        </LineChart>
      </ResponsiveContainer>
    );
  }

  if (type === 'area') {
    return (
      <ResponsiveContainer width="100%" height={height}>
        <AreaChart data={data} margin={cartesianMargin}>
          <defs>
            <linearGradient id="rbArea" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#6366f1" stopOpacity={0.7} />
              <stop offset="95%" stopColor="#6366f1" stopOpacity={0.05} />
            </linearGradient>
          </defs>
          {showGrid && <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />}
          <XAxis dataKey="name" fontSize={11} tick={{ fill: '#64748b' }} label={xLabelProp} />
          <YAxis fontSize={11} tick={{ fill: '#64748b' }} tickFormatter={fmt} label={yLabelProp} />
          {showTooltip && <Tooltip formatter={(v) => fmt(v)} />}
          {showLegend && <Legend wrapperStyle={{ fontSize: 11 }} />}
          <Area type="monotone" dataKey="value" stroke="#6366f1" strokeWidth={2} fill="url(#rbArea)" name={yName} />
          {goalLine}
        </AreaChart>
      </ResponsiveContainer>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} margin={cartesianMargin}>
        {showGrid && <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />}
        <XAxis dataKey="name" fontSize={11} tick={{ fill: '#64748b' }} label={xLabelProp} />
        <YAxis fontSize={11} tick={{ fill: '#64748b' }} tickFormatter={fmt} label={yLabelProp} />
        {showTooltip && <Tooltip formatter={(v) => fmt(v)} cursor={{ fill: 'rgba(99,102,241,0.06)' }} />}
        {showLegend && <Legend wrapperStyle={{ fontSize: 11 }} />}
        <Bar dataKey="value" radius={[6, 6, 0, 0]} name={yName}>
          {data.map((d, i) => <Cell key={d.name} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
        </Bar>
        {goalLine}
      </BarChart>
    </ResponsiveContainer>
  );
};

export default ChartRenderer;
